import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Home, Calculator, Timer, Microscope, Calendar, Plus, BookOpen, Music, Palette } from "lucide-react";
import { useTranslation } from "react-i18next";

const SchoolModeWidget: React.FC = () => {
    const { t } = useTranslation("nonHealth");
    
    const [classData, setClassData] = useState({
        current: "math",
        next: "science",
        nextTime: "۱۰:۰۰",
        remaining: 25,
        progress: 60
    });

    useEffect(() => {
        const subjects = ["math", "science", "history", "art", "physics"];
        const currentIdx = Math.floor(Math.random() * subjects.length);
        const nextIdx = (currentIdx + 1) % subjects.length;
        const remaining = Math.floor(Math.random() * 45) + 1;
        
        setClassData({
            current: subjects[currentIdx],
            next: subjects[nextIdx],
            nextTime: `${Math.floor(Math.random() * 4 + 8)}:00`,
            remaining: remaining,
            progress: 100 - (remaining / 60 * 100)
        });
    }, []);

    const getIcon = (subject: string) => {
        switch(subject) {
            case "math": return <Calculator />;
            case "science": return <Microscope size={16} />;
            case "history": return <BookOpen />;
            case "art": return <Palette />;
            case "physics": return <Timer />;
            default: return <Calculator />;
        }
    };

    return (
        <motion.div
            key="schoolMode-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
        >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-purple-100 relative overflow-hidden">
                {/* افکت‌های تزئینی پس‌زمینه */}
                <motion.div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-gradient-to-br from-purple-200/30 to-indigo-300/30 rounded-full blur-xl"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        repeatType: "mirror",
                    }}
                />

                {/* هدر ویجت */}
                <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 p-4 rounded-xl mb-3 shadow-inner relative overflow-hidden">
                    <motion.div
                        className="absolute top-2 right-2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-indigo-700"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <motion.div
                            className="text-indigo-700"
                            animate={{ y: [0, -2, 0] }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                repeatType: "loop",
                            }}
                        >
                            <Home size={20} />
                        </motion.div>
                    </motion.div>

                    <motion.h5
                        className="text-black/80 font-bold text-lg mb-3 pr-12 relative"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <span className="relative">
                            {t("widgets.schoolMode.activeTitle")}
                            <motion.span
                                className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400 to-yellow-500"
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ duration: 0.8, delay: 0.5 }}
                            />
                        </span>
                    </motion.h5>

                    {/* کلاس فعلی */}
                    <motion.div
                        className="flex gap-2 items-center mb-3 bg-white/70 p-3 rounded-lg shadow-sm border border-purple-100"
                        whileHover={{
                            y: -2,
                            boxShadow: "0 4px 8px rgba(147, 51, 234, 0.15)",
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                    >
                        <div className="relative flex items-center justify-center w-10 h-10">
                            <motion.div className="absolute inset-0 bg-amber-100 rounded-full" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 3, repeat: Infinity }} />
                            <motion.div className="text-xl relative z-10">
                                {getIcon(classData.current)}
                            </motion.div>
                        </div>
                        <div className="ml-2">
                            <p className="text-indigo-700 text-sm font-medium">{t("widgets.schoolMode.currentClass")}</p>
                            <motion.p
                                className="font-bold text-black/80 text-lg"
                                animate={{ scale: [1, 1.03, 1] }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    repeatType: "mirror",
                                }}
                            >
                                {t(`widgets.schoolMode.${classData.current}`)}
                            </motion.p>
                        </div>
                        <motion.div
                            className="w-3 h-3 bg-green-500 rounded-full ml-auto relative"
                            animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <motion.div
                                className="absolute inset-0 bg-green-500 rounded-full"
                                animate={{ scale: [1, 1.8, 1], opacity: [0.5, 0, 0.5] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            />
                        </motion.div>
                    </motion.div>

                    {/* نوار پیشرفت زمان */}
                    <div className="mb-1">
                        <div className="flex justify-between items-center mb-1.5">
                            <p className="text-indigo-700 text-xs font-medium">{t("widgets.schoolMode.remainingTime")}</p>
                            <motion.div className="text-black/80 font-bold bg-white px-3 py-1 rounded-md flex items-center gap-1 shadow-sm border border-purple-100" whileHover={{ scale: 1.05 }}>
                                <motion.div
                                    animate={{ rotate: [0, 360] }}
                                    transition={{
                                        duration: 20,
                                        repeat: Infinity,
                                        ease: "linear",
                                    }}
                                >
                                    <Timer size={16} />
                                </motion.div>
                                <span>{t("widgets.schoolMode.minutes", { count: classData.remaining })}</span>
                            </motion.div>
                        </div>
                        <div className="relative w-full bg-white rounded-full h-2 overflow-hidden shadow-inner">
                            <motion.div
                                className="h-full rounded-full bg-gradient-to-r from-amber-400 to-yellow-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${classData.progress}%` }}
                                transition={{ duration: 1.5, ease: "easeOut" }}
                            />
                        </div>
                    </div>
                </div>

                {/* کارت‌های اطلاعاتی */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <motion.div
                        className="bg-white p-3 rounded-lg shadow-sm border border-purple-100 relative overflow-hidden"
                        whileHover={{
                            y: -4,
                            boxShadow: "0 8px 16px rgba(147, 51, 234, 0.15)",
                        }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.5 }}
                    >
                        <div className="text-indigo-700 font-medium mb-1 flex items-center gap-1.5">
                            <motion.div
                                className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center"
                                animate={{ rotate: [0, 10, -10, 0] }}
                                transition={{ duration: 3, repeat: Infinity }}
                            >
                                <Microscope size={16} />
                            </motion.div>
                            <span>{t("widgets.schoolMode.nextClass")}</span>
                        </div>
                        <p className="text-gray-700 font-semibold">{t("widgets.schoolMode.nextClassInfo", { subject: t(`widgets.schoolMode.${classData.next}`), time: classData.nextTime })}</p>
                    </motion.div>

                    <motion.div
                        className="bg-white p-3 rounded-lg shadow-sm border border-indigo-100 relative overflow-hidden"
                        whileHover={{
                            y: -4,
                            boxShadow: "0 8px 16px rgba(79, 70, 229, 0.15)",
                        }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.6 }}
                    >
                        <div className="text-indigo-700 font-medium mb-1 flex items-center gap-1.5">
                            <motion.div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                                <Calendar size={16} />
                            </motion.div>
                            <span>{t("widgets.schoolMode.scheduleStatus")}</span>
                        </div>
                        <p className="text-gray-700 font-semibold">{t("widgets.schoolMode.weeklyScheduleActive")}</p>
                    </motion.div>
                </div>

                {/* دکمه تعاملی */}
                <motion.button
                    className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 text-white py-2.5 rounded-lg text-sm font-medium shadow-md relative overflow-hidden"
                    whileHover={{
                        scale: 1.02,
                        boxShadow: "0 4px 12px rgba(147, 51, 234, 0.25)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.7 }}
                >
                    <motion.span className="relative flex items-center justify-center gap-2">
                        <motion.div animate={{ rotate: [0, 15, -15, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                            <Plus size={16} color="white" />
                        </motion.div>
                        {t("widgets.schoolMode.settings")}
                    </motion.span>
                </motion.button>
            </div>
        </motion.div>
    );
};

export default SchoolModeWidget;
